import { IDENTITY, NO_ADJUSTMENTS, SCHEMA_VERSION, type Color, type Document, type Matrix, type Node, type NodeOf, type NodeType, type Paint, type Stroke } from "./document";

const ALPHABET = "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";

/** 16-char random id (~95 bits), URL-safe, works in browsers, Workers and Node. */
export function newId(len = 16): string {
  const bytes = crypto.getRandomValues(new Uint8Array(len));
  let s = "";
  for (const b of bytes) s += ALPHABET[b % 62];
  return s;
}

export const solid = (color: Color): Paint => ({ type: "solid", color });

export function stroke(color: Color, width: number, partial: Partial<Stroke> = {}): Stroke {
  return { paint: solid(color), width, align: "center", dash: [], cap: "butt", join: "miter", ...partial };
}

export function createDocument(opts: { name: string; width: number; height: number; id?: string }): Document {
  return {
    schemaVersion: SCHEMA_VERSION,
    id: opts.id ?? newId(),
    name: opts.name,
    pages: [{ id: newId(), name: "Page 1", width: opts.width, height: opts.height, background: solid("#ffffffff"), children: [], guides: [] }],
    nodes: {},
    assets: {},
  };
}

type Common = "id" | "name" | "parentId" | "transform" | "opacity" | "blendMode" | "visible" | "locked" | "effects" | "warp";
type Specific<T extends NodeType> = Omit<NodeOf<T>, Common | "type">;

const DEFAULTS: { [T in NodeType]: Specific<T> } = {
  rect: { width: 200, height: 200, cornerRadius: 0, fills: [solid("#c4c7ceff")], strokes: [] },
  ellipse: { width: 200, height: 200, fills: [solid("#c4c7ceff")], strokes: [] },
  polygon: { width: 200, height: 200, sides: 6, cornerRadius: 0, fills: [solid("#c4c7ceff")], strokes: [] },
  star: { width: 200, height: 200, points: 5, innerRatio: 0.45, fills: [solid("#c4c7ceff")], strokes: [] },
  line: { width: 240, height: 0, routing: "straight", strokes: [stroke("#1f2329ff", 4, { cap: "round" })], start: "none", end: "none" },
  frame: { width: 300, height: 300, cmds: [0, 0, 0, 1, 1, 0, 1, 1, 1, 1, 0, 1, 5], cornerRadius: 0, children: [], fills: [solid("#d9dbe1ff")], strokes: [] },
  image: { width: 100, height: 100, assetId: "", crop: { x: 0, y: 0, w: 1, h: 1 }, cornerRadius: 0, strokes: [], adjust: NO_ADJUSTMENTS, filter: null, duotone: null },
  group: { children: [] },
  raster: { width: 1080, height: 1080, pixelWidth: 1080, pixelHeight: 1080, tileSize: 256, tiles: null },
  adjustment: { width: 1080, height: 1080, adjustment: { kind: "brightnessContrast", brightness: 0, contrast: 0 } },
  path: { width: 100, height: 100, cmds: [], fillRule: "nonzero", fills: [solid("#c4c7ceff")], strokes: [] },
  text: {
    width: 400,
    height: 60,
    sizing: "auto-width",
    spans: [{ text: "Add a heading", style: defaultTextStyle() }],
    align: "left",
    lineHeight: 1.4,
    effect: { type: "none" },
    curve: 0,
    list: "none",
  },
};

export function defaultTextStyle(partial: Partial<import("./document").TextStyle> = {}): import("./document").TextStyle {
  return { fontFamily: "Inter", fontWeight: 400, italic: false, fontSize: 48, fill: solid("#1f2329ff"), letterSpacing: 0, underline: false, strikethrough: false, uppercase: false, ...partial };
}

/** Default layer names per node type. */
export const NODE_LABELS: Record<NodeType, string> = {
  rect: "Rectangle",
  ellipse: "Ellipse",
  polygon: "Polygon",
  star: "Star",
  line: "Line",
  image: "Photo",
  group: "Group",
  text: "Text",
  path: "Path",
  frame: "Frame",
  raster: "Raster layer",
  adjustment: "Adjustment",
};

/** A new node with sensible defaults; `props` overrides anything. */
export function createNode<T extends NodeType>(type: T, parentId: string, props: Partial<NodeOf<T>> & { transform?: Matrix } = {}): NodeOf<T> {
  return {
    id: newId(),
    type,
    name: NODE_LABELS[type],
    parentId,
    transform: [...IDENTITY] as Matrix,
    opacity: 1,
    blendMode: "normal",
    visible: true,
    locked: false,
    effects: [],
    warp: null,
    ...structuredClone(DEFAULTS[type]),
    ...props,
  } as unknown as NodeOf<T>;
}

export function isContainer(n: Node): n is NodeOf<"group"> | NodeOf<"frame"> {
  return n.type === "group" || n.type === "frame";
}

/** "#rrggbbaa" -> [r, g, b, a] in 0..1. */
export function parseColor(c: Color): [number, number, number, number] {
  const n = parseInt(c.slice(1), 16);
  return [((n >>> 24) & 255) / 255, ((n >>> 16) & 255) / 255, ((n >>> 8) & 255) / 255, (n & 255) / 255];
}

export function formatColor(r: number, g: number, b: number, a = 1): Color {
  const h = (v: number) => Math.round(Math.min(1, Math.max(0, v)) * 255).toString(16).padStart(2, "0");
  return `#${h(r)}${h(g)}${h(b)}${h(a)}`;
}

/** Accept #rgb, #rrggbb, #rrggbbaa (any case) and normalise. */
export function normalizeColor(input: string): Color | null {
  const s = input.trim().toLowerCase().replace(/^#/, "");
  if (/^[0-9a-f]{3}$/.test(s)) return `#${[...s].map((c) => c + c).join("")}ff`;
  if (/^[0-9a-f]{6}$/.test(s)) return `#${s}ff`;
  if (/^[0-9a-f]{8}$/.test(s)) return `#${s}`;
  return null;
}
